import RadioItem from "@/components/Radio/RadioItem";
import { Controller, useFormContext } from "react-hook-form";
import TextField from "@/components/TextField/TextField";
import AutoComplete from "@/components/Autocomplete";
import AvatarDetails from "@/components/AvatarDetails";
import Select from "@/components/Select";
import MultiSelect from "@/components/MultiSelect";
import { GET_PEOPLE, RECRUITMENT_EVENT_OPTIONS_QUERY } from "@/pages/events/queries";
import { useQuery } from "@apollo/client";
import { useState } from "react";
import { RecruitmentEventOptionsQueryResponse } from "@/pages/events/types";
import { NumberFormatBase } from "react-number-format";
import { currencyFormatter } from "@/lib/currencyHelpers";
import { useStepVisibility } from "../FormVisibilityContext";

interface PeopleData {
  pgaId: string;
  displayName: string;
  profilePhoto: string;
  id: string;
}

const ModulesStep = () => {
  const { control, setValue, watch } = useFormContext();
  const eventType = watch("eventType");
  const [searchTerm, setSearchTerm] = useState("");
  const { disabled } = useStepVisibility();

  const handleAttendanceToggle = (value: boolean) => {
    setValue("collectsAttendance", value);
  };

  const { data: peopleData } = useQuery(GET_PEOPLE, {
    variables: {
      input: {
        search: searchTerm
      },
      first: 10
    },
    skip: searchTerm.length < 3
  });

  const { data: recruitmentOptions } = useQuery<RecruitmentEventOptionsQueryResponse>(RECRUITMENT_EVENT_OPTIONS_QUERY, {
    skip: eventType.name !== "Recruitment"
  });

  const mapPeople = (data: PeopleData[]) =>
    (data || []).map((person) => {
      return {
        name: person?.displayName || "",
        pgaId: person?.pgaId || "",
        profilePhoto: person?.profilePhoto || "",
        id: person?.id || ""
      };
    });

  const peopleOptions = mapPeople(peopleData?.people?.nodes || []);

  const renderAutoComplete = (fieldName: string, label: string) => (
    <Controller
      control={control}
      name={fieldName}
      render={({ field }) => (
        <AutoComplete
          label={label}
          data={peopleOptions}
          {...field}
          query={field.value?.name || ""}
          renderItem={(item) => <AvatarDetails picture={item.profilePhoto} title={item.name} text={item.pgaId} />}
          onChangeText={(text) => {
            field.onChange({ name: text, value: "" });
            setSearchTerm(text);
          }}
          reset={() => {
            field.onChange(null);
            setSearchTerm("");
          }}
          onSelect={(item) => {
            field.onChange(item);
            setSearchTerm("");
          }}
          menuClassName="max-h-[450px] max-w-[450px]"
        />
      )}
    />
  );

  return (
    <fieldset disabled={disabled} className="flex flex-col gap-6 mb-0">
      <p className="text-headline-small">Setup additional modules that should be utilized for managing this event.</p>
      {eventType.name === "PAT" ? (
        <div className="flex flex-col gap-3">
          <p className="text-title-medium">Playing Ability Test Scoring Settings</p>
          <p className="text-body-medium">These items can be edited later in event settings.</p>
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-2 gap-2">
              <Controller
                control={control}
                name="patEventAttributes.yardageMale"
                render={({ field }) => <TextField type="number" label="Yardage - Male" placeholder="" {...field} />}
              />
              <Controller
                control={control}
                name="patEventAttributes.yardageMaleFiftyPlus"
                render={({ field }) => <TextField type="number" label="Yardage - Male 50+" placeholder="" {...field} />}
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Controller
                control={control}
                name="patEventAttributes.courseRatingMale"
                render={({ field }) => (
                  <TextField type="number" label="Course Rating - Male" placeholder="" {...field} />
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Controller
                control={control}
                name="patEventAttributes.yardageFemale"
                render={({ field }) => <TextField type="number" label="Yardage - Female" placeholder="" {...field} />}
              />
              <Controller
                control={control}
                name="patEventAttributes.yardageFemaleFiftyPlus"
                render={({ field }) => (
                  <TextField type="number" label="Yardage - Female 50+" placeholder="" {...field} />
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Controller
                control={control}
                name="patEventAttributes.courseRatingFemale"
                render={({ field }) => (
                  <TextField type="number" label="Course Rating - Female" placeholder="" {...field} />
                )}
              />
            </div>

            <div className="w-full">
              {renderAutoComplete("patEventAttributes.hostProfessional", "Host Professional")}
            </div>

            <div className="w-full">
              {renderAutoComplete("patEventAttributes.examiner", "Examiner")}
              <span className="text-body-small text-sys-brand-on-surface-variant ml-4">
                PAT Examiner will be responsible for tracking attendance and scoring
              </span>
            </div>
          </div>
        </div>
      ) : eventType.name === "Recruitment" ? (
        <div className="flex flex-col gap-2">
          <p className="text-title-medium">Recruitment Module</p>
          <p className="text-body-medium">These items can be edited later in event settings.</p>
          <div className="flex flex-col mt-6 gap-6">
            {/* Basic Event Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Controller
                control={control}
                name="recruitmentEventAttributes.recruitmentEventType"
                render={({ field }) => (
                  <Select
                    label="Recruitment Event Type"
                    required
                    options={
                      recruitmentOptions?.recruitmentEventTypeOptions?.enumValues?.map((option) => ({
                        value: option.name,
                        displayText: option.description
                      })) || []
                    }
                    value={field.value}
                    onSelect={(value) => field.onChange(value)}
                    className="w-full"
                  />
                )}
              />
              <Controller
                control={control}
                name="recruitmentEventAttributes.purpose"
                render={({ field }) => (
                  <Select
                    label="Purpose"
                    required
                    options={
                      recruitmentOptions?.purposeOptions?.enumValues?.map((option) => ({
                        value: option.name,
                        displayText: option.description
                      })) || []
                    }
                    value={field.value}
                    onSelect={(value) => field.onChange(value)}
                    className="w-full"
                  />
                )}
              />
            </div>

            <div className="w-full">
              <Controller
                control={control}
                name="recruitmentEventAttributes.pgaRepresentation"
                rules={{
                  required: {
                    value: true,
                    message: "PGA Representation is required"
                  }
                }}
                render={({ field }) => (
                  <MultiSelect
                    label="PGA Representation"
                    required
                    options={
                      recruitmentOptions?.pgaRepresentationOptions?.enumValues?.map((option) => ({
                        value: option.name,
                        label: option.description
                      })) || []
                    }
                    selectedValues={field.value || []}
                    onSelect={(value, checked) => {
                      const currentValues = field.value || [];
                      if (checked) {
                        field.onChange([...currentValues, value]);
                      } else {
                        field.onChange(currentValues.filter((v: string) => v !== value));
                      }
                    }}
                    onClear={() => field.onChange([])}
                    className="w-full"
                  />
                )}
              />
            </div>

            <div className="w-full">
              <Controller
                control={control}
                name="recruitmentEventAttributes.supportRequested"
                render={({ field }) => (
                  <MultiSelect
                    label="Support Requested"
                    options={
                      recruitmentOptions?.supportRequestedOptions?.enumValues?.map((option) => ({
                        value: option.name,
                        label: option.description
                      })) || []
                    }
                    selectedValues={field.value || []}
                    onSelect={(value, checked) => {
                      const currentValues = field.value || [];
                      if (checked) {
                        field.onChange([...currentValues, value]);
                      } else {
                        field.onChange(currentValues.filter((v: string) => v !== value));
                      }
                    }}
                    onClear={() => field.onChange([])}
                    className="w-full"
                  />
                )}
              />
              <p className="text-body-small text-sys-brand-on-surface-variant mt-2">
                Please select any additional support you need. Upon approval, individuals requested will be notified and
                confirm their availability and attendance.
              </p>
            </div>

            <div className="w-full">
              <Controller
                control={control}
                name="recruitmentEventAttributes.strategicFunding"
                render={({ field }) => (
                  <Select
                    label="Strategic Funding"
                    required
                    options={
                      recruitmentOptions?.strategicFundingOptions?.enumValues?.map((option) => ({
                        value: option.name,
                        displayText: option.description
                      })) || []
                    }
                    value={field.value}
                    onSelect={(value) => field.onChange(value)}
                    className="w-full"
                  />
                )}
              />
              <p className="text-body-small text-sys-brand-on-surface-variant mt-2">
                Event is being used for strategic funding
              </p>
            </div>

            {/* Financial Information */}
            <div className="grid grid-cols-2 gap-4">
              <Controller
                control={control}
                name="recruitmentEventAttributes.estimatedExpenses"
                render={({ field }) => (
                  <NumberFormatBase
                    label="Estimated Expenses"
                    className="w-full"
                    value={field.value || 0}
                    customInput={TextField}
                    onValueChange={(values) => {
                      field.onChange(values.floatValue || 0);
                    }}
                    format={(value) => `$${currencyFormatter(value)}`}
                  />
                )}
              />
              <Controller
                control={control}
                name="recruitmentEventAttributes.sponsorshipAsk"
                render={({ field }) => (
                  <NumberFormatBase
                    label="Sponsorship Ask"
                    className="w-full"
                    value={field.value || 0}
                    customInput={TextField}
                    onValueChange={(values) => {
                      field.onChange(values.floatValue || 0);
                    }}
                    format={(value) => `$${currencyFormatter(value)}`}
                  />
                )}
              />
            </div>

            {/* Estimated Attendance */}
            <div className="w-full">
              <p className="text-title-medium mb-4">Estimated Attendance</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Controller
                  control={control}
                  name="recruitmentEventAttributes.estimatedParents"
                  render={({ field }) => <TextField type="number" label="Parents" placeholder="0" {...field} />}
                />
                <Controller
                  control={control}
                  name="recruitmentEventAttributes.estimatedCoaches"
                  render={({ field }) => <TextField type="number" label="Coaches" placeholder="0" {...field} />}
                />
                <Controller
                  control={control}
                  name="recruitmentEventAttributes.estimatedOtherAttendees"
                  render={({ field }) => (
                    <TextField type="number" label="Students/Athletes/Attendees" placeholder="0" {...field} />
                  )}
                />
              </div>
            </div>

            {/* Additional Comments */}
            <div className="w-full">
              <Controller
                control={control}
                name="recruitmentEventAttributes.additionalComments"
                render={({ field }) => (
                  <TextField
                    label="Additional Comments"
                    placeholder="Enter any additional comments..."
                    {...field}
                    className="w-full"
                  />
                )}
              />
            </div>
          </div>
        </div>
      ) : (
        <>
          <p className="text-title-medium mb-0">Attendance Module</p>

          <Controller
            control={control}
            name="collectsAttendance"
            data-testid="attendance-toggle"
            render={({ field }) => (
              <div className="flex flex-col">
                <div className="py-0">
                  <div className="mb-3 ml-7">
                    <RadioItem
                      name="attendance-no-tracking"
                      headline="No Tracking"
                      checked={!field.value}
                      onChange={() => handleAttendanceToggle(false)}
                    />
                  </div>
                </div>
                <div className="py-0">
                  <div className="mb-3 ml-7">
                    <RadioItem
                      name="attendance-tracking"
                      headline="Basic Attendance Tracking"
                      supportingText="Allow event hosts to track the attendance of each registrant per session"
                      checked={field.value}
                      onChange={() => handleAttendanceToggle(true)}
                    />
                  </div>
                </div>
              </div>
            )}
          />
        </>
      )}
    </fieldset>
  );
};

export default ModulesStep;
